<template>
  <span class="view-tag" :class="`view-tag--${view}`" :title="label" :aria-label="label">
    <svg
      class="view-tag__icon"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        :d="icon"
        stroke="currentColor"
        stroke-width="2.6"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
    <!-- Both spellings ship; CSS picks one, so rotation and split-screen are
         handled without a resize listener. aria-hidden on both: the accessible
         name is the full phrase on the root, whichever one is painted. -->
    <span class="view-tag__label view-tag__label--full" aria-hidden="true">{{ label }}</span>
    <span class="view-tag__label view-tag__label--short" aria-hidden="true">{{ shortLabel }}</span>
  </span>
</template>

<script>
// Which guide the reader is looking at, said out loud in the top nav (ONE-8241).
//
// A leader reads two different documents behind one lesson — the Student's Guide
// and their own — and the only thing that told them which was the toggle in the
// sidebar, several taps away. Highlights and notes are now scoped per view too
// (ADR-0019), so "which view am I in" decides where a mark they make will live,
// and that has to be visible without going looking for it.
//
// Not interactive: it states the current view, it does not change it. The toggle
// keeps that job — a tag that also switched would be an easy thing to hit by
// accident while reading, and it would move a leader's annotations to the other
// guide as a side effect.
//
// Labels and icons are the design's `modeBadgeText` / `modeBadgeIcon` verbatim
// (`For Review (Devs).dc.html`, assigned in renderVals()): the full two-word
// phrase, a person glyph for the student guide and a star for the leader's.
//
// The full phrase does not fit every phone. Measured with the app's own Proxima
// Nova against this bar's real geometry (20px insets, 44px hit targets, 8px
// gaps, five controls): "STUDENT VIEW" first fits at 401px of viewport and
// "LEADER VIEW" at 392px. The design's frame is 402px, so it fits there by 1px
// and nowhere below — 375 (SE, 13 mini), 390 (12-14) and 393 (15/16) all
// overflow, by 26px at 375. The bar cannot buy that back: its 44px hit targets
// are the accessibility floor and its 20px insets are ONE-8234's.
// So the full phrase is what shows wherever the design's own width holds, and
// one word below it. An ellipsised status label — "STUDENT VI..." — tells the
// reader nothing, so shortening beats truncating.
const VIEWS = {
  student: {
    label: 'Student View',
    shortLabel: 'Student',
    icon: 'M12 14a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM5 21a7 7 0 0 1 14 0',
  },
  leader: {
    label: 'Leader View',
    shortLabel: 'Leader',
    icon: 'M12 3l2.6 5.3 5.9.8-4.3 4.1 1 5.8L12 16l-5.2 3 1-5.8-4.3-4.1 5.9-.8z',
  },
};

export default {
  name: 'ViewTag',
  props: {
    view: {
      type: String,
      required: true,
      validator: value => value === 'student' || value === 'leader',
    },
  },
  computed: {
    // The prop validator rejects anything else in dev; production still has to
    // render a real badge rather than "undefined".
    spec() {
      return VIEWS[this.view] ?? VIEWS.student;
    },
    label() {
      return this.spec.label;
    },
    shortLabel() {
      return this.spec.shortLabel;
    },
    icon() {
      return this.spec.icon;
    },
  },
};
</script>

<style scoped>
/* Chrome copied from the design's reader mode badge (`For Review (Devs).dc.html`,
   the `modeBadgeOn` span): an 8px rounded rect — not a pill — on the neutral
   ground, 5px/10px padding, 5px gap, 10.5px 700-weight uppercase at 0.08em.
   Small and quiet: it is a status label sitting in a bar full of controls, and
   must not read as another button. */
.view-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  padding: 5px 10px;
  border-radius: 8px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
  /* No line-height override: the design leaves the label on its natural leading,
     which is what centres it against the icon rather than riding high in the box. */
}

/* 10px, drawn at the design's 2.6 stroke — which is ~1.1px once the 24-unit
   viewBox is scaled down. Both glyphs span most of the viewBox, so their optical
   centre lands on the label's; the triangle this replaced only occupied y 4→12
   and so sat in the top half of its own box. */
.view-tag__icon {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
}

/* 410px is the `2xs` breakpoint from tailwind.config.js, and the first one clear
   of the 401px the wider label measured at — 402 would fit on the nose with no
   headroom for a font that renders a hair wider. */
.view-tag__label--full {
  display: none;
}

@media (min-width: 410px) {
  .view-tag__label--full {
    display: inline;
  }

  .view-tag__label--short {
    display: none;
  }
}

/* One ground for both guides, per the design: its badge hardcodes the neutral
   muted/muted-fg pair regardless of mode, and lets the icon and the word carry
   the difference. (The yellow leader ground this replaced was ours, not the
   design's.) `--courses-ink-subtle` already collapses onto the dark ink in the
   dark scope, so the pair works in both themes without a dark override. */
.view-tag--student,
.view-tag--leader {
  background-color: var(--courses-surface-sunken);
  color: var(--courses-ink-subtle);
}
</style>
